/*
 * Live 3D fabric mockup renderer.
 *
 * Re-textures a Blender render in the browser so the garment mockups follow the
 * Adjust Scale slider instead of showing one fixed print size. The lighting,
 * folds, weave and backdrop are Blender's; only the print underneath changes.
 *
 * Per pixel this reproduces EEVEE's composition:
 *     linear  = mix(backplate, print * diffuseLight + specular, coverage)
 *     linear += bloom(linear)
 *     display = LUT(shaper(linear))
 *
 * Bloom matters. It is a screen-space post-process, so it is absent from the
 * baked Diffuse/Specular Light passes; without it the live mockup is visibly
 * darker than the gallery JPEGs (32/255 mean error, plainly visible). Adding it
 * back brings that to ~8/255.
 *
 * Usage (see product-images-Evergreen.liquid):
 *     var r = RCFMockup.create(960);
 *     await r.load(config);
 *     await r.setPatternUrl(designUrl);
 *     ctx.drawImage(r.render(inchesPerRepeat), 0, 0, w, h);
 *
 * Everything fails soft: no WebGL2, a 404, or no baked mockup for the image and
 * the caller draws the static JPEG instead.
 */
window.RCFMockup = (function () {
  'use strict';

  var VERT =
    '#version 300 es\n' +
    'out vec2 vTex;\n' +
    'void main() {\n' +
    '  vec2 p = vec2(float((gl_VertexID << 1) & 2), float(gl_VertexID & 2));\n' +
    '  vTex = vec2(p.x, 1.0 - p.y);\n' +
    '  gl_Position = vec4(p * 2.0 - 1.0, 0.0, 1.0);\n' +
    '}';

  /* Pass 1 - the composite, in linear light. No display transform yet; bloom
     has to happen in scene-referred values the way EEVEE does it. */
  var FRAG_COMPOSITE =
    '#version 300 es\n' +
    'precision highp float;\n' +
    'in vec2 vTex;\n' +
    'out vec4 fragColor;\n' +
    'uniform sampler2D uUV, uLit, uSpec, uBack, uPattern;\n' +
    'uniform vec2 uUVOffset, uUVScale;\n' +
    'uniform float uLitK, uSpecK, uBackK, uRepeats, uRotation, uSeamLimit;\n' +
    'uniform float uFabricGain, uFabricKnee, uShoulder, uFabricWhite;\n' +
    'uniform bool uHasSpec;\n' +
    // Each UV axis is packed across two 8-bit channels; 8 bits alone gives 256
    // steps across a whole garment, which stair-steps visibly.
    'vec2 decodeUV(vec4 c) {\n' +
    '  float u = (floor(c.r * 255.0 + 0.5) * 256.0 + floor(c.g * 255.0 + 0.5)) / 65535.0;\n' +
    '  float v = (floor(c.b * 255.0 + 0.5) * 256.0 + floor(c.a * 255.0 + 0.5)) / 65535.0;\n' +
    '  return vec2(u, v) * uUVScale + uUVOffset;\n' +
    '}\n' +
    'void main() {\n' +
    '  vec2 uv = decodeUV(texture(uUV, vTex));\n' +
    '  float cs = cos(uRotation), sn = sin(uRotation);\n' +
    '  vec2 tc = vec2(uv.x * cs - uv.y * sn, uv.x * sn + uv.y * cs) * uRepeats;\n' +
    /* Blender's texture origin is bottom-left, the browser's is top-left, so V
       has to be flipped. Doing it here rather than with UNPACK_FLIP_Y_WEBGL is
       deliberate: that flag is silently IGNORED for ImageBitmap sources (though
       honoured for <img> and canvas), which put the print on upside down.
       Negating V is equivalent to 1-V under REPEAT wrapping, and keeps the
       derivatives below consistent. */
    '  vec2 pt = vec2(tc.x, -tc.y);\n' +
    // Derivatives explode where UV islands meet, which picks the smallest mip
    // and draws a dark line along every seam.
    '  vec2 dx = dFdx(pt), dy = dFdy(pt);\n' +
    '  float lim = uRepeats * uSeamLimit;\n' +
    '  float lx = length(dx), ly = length(dy);\n' +
    '  if (lx > lim) dx *= lim / lx;\n' +
    '  if (ly > lim) dy *= lim / ly;\n' +
    '  vec3 print = textureGrad(uPattern, pt, dx, dy).rgb;\n' +
    '  vec4 litTexel = texture(uLit, vTex);\n' +
    '  vec3 lit = litTexel.rgb * uLitK;\n' +
    '  float cov = litTexel.a;\n' +
    '  vec3 spec = uHasSpec ? texture(uSpec, vTex).rgb * uSpecK : vec3(0.0);\n' +
    '  vec3 back = texture(uBack, vTex).rgb * uBackK;\n' +
    /* The print should read as brightly on the garment as on the flat swatch.
       Studio lighting peaks near 0.55 linear and the High Contrast output look
       pushes mid-tones down further, so a faithful render undersells the
       fabric. uFabricGain lifts it; the shoulder then compresses what would
       otherwise clip, so brightened highlights keep their fold shading.
       gain 1.0 / shoulder 0.0 is an exact no-op and reproduces Blender. */
    '  vec3 fab = print * lit * uFabricGain + spec;\n' +
    '  vec3 over = max(fab - uFabricKnee, vec3(0.0));\n' +
    '  fab = min(fab, vec3(uFabricKnee)) + over / (1.0 + over * uShoulder);\n' +
    '  fab *= uFabricWhite;\n' +
    '  fragColor = vec4(mix(back, fab, cov), cov);\n' +
    '}';

  /* Pass 2 - soft-knee threshold, the source for the blur. */
  var FRAG_PREFILTER =
    '#version 300 es\n' +
    'precision highp float;\n' +
    'in vec2 vTex;\n' +
    'out vec4 fragColor;\n' +
    'uniform sampler2D uSrc;\n' +
    'uniform vec4 uCurve;\n' +   // x: threshold-knee, y: 2*knee, z: 0.25/knee, w: threshold
    'uniform float uClamp;\n' +
    'void main() {\n' +
    // uSrc is a render target, so its row 0 is the bottom of the image while
    // vTex treats row 0 as the top. Without this flip the bloom buffer ends up
    // mirrored against the composite and paints ghost garments in the backdrop.
    '  vec3 c = texture(uSrc, vec2(vTex.x, 1.0 - vTex.y)).rgb;\n' +
    '  if (uClamp > 0.0) c = min(c, vec3(uClamp));\n' +
    '  float br = max(c.r, max(c.g, c.b));\n' +
    '  float soft = clamp(br - uCurve.x, 0.0, uCurve.y);\n' +
    '  soft = soft * soft * uCurve.z;\n' +
    '  float mul = max(soft, br - uCurve.w) / max(br, 1e-5);\n' +
    '  fragColor = vec4(c * mul, 1.0);\n' +
    '}';

  /* Pass 3 - add the blurred bloom, then Blender's display transform. */
  var FRAG_FINAL =
    '#version 300 es\n' +
    'precision highp float;\n' +
    'in vec2 vTex;\n' +
    'out vec4 fragColor;\n' +
    'uniform sampler2D uLinear, uBloom, uLUT;\n' +
    'uniform float uLutSize, uBloomIntensity, uBloomLevels;\n' +
    'uniform vec2 uLogRange;\n' +
    /* Render targets are stored bottom-up: texel row 0 of an FBO is the bottom
       of the image, whereas row 0 of an uploaded PNG is the top. Sampling both
       with the same coordinate flips the composite relative to the passes. */
    'vec2 fboUV() { return vec2(vTex.x, 1.0 - vTex.y); }\n' +
    'vec3 shaper(vec3 x) {\n' +
    '  return clamp((log2(max(x, vec3(1e-8))) - uLogRange.x) / (uLogRange.y - uLogRange.x), 0.0, 1.0);\n' +
    '}\n' +
    'vec3 applyLUT(vec3 s) {\n' +
    '  float N = uLutSize;\n' +
    '  float bz = s.b * (N - 1.0);\n' +
    '  float b0 = floor(bz), b1 = min(b0 + 1.0, N - 1.0), f = bz - b0;\n' +
    '  float x = s.r * (N - 1.0) + 0.5, y = s.g * (N - 1.0) + 0.5;\n' +
    '  vec3 c0 = texture(uLUT, vec2((b0 * N + x) / (N * N), y / N)).rgb;\n' +
    '  vec3 c1 = texture(uLUT, vec2((b1 * N + x) / (N * N), y / N)).rgb;\n' +
    '  return mix(c0, c1, f);\n' +
    '}\n' +
    // EEVEE walks a real blur pyramid; averaging mip levels is a close stand-in
    // at a fraction of the passes. The parameters are fitted to compensate.
    'vec3 gatherBloom() {\n' +
    '  vec3 sum = vec3(0.0);\n' +
    '  float n = 0.0;\n' +
    '  for (int i = 1; i <= 8; i++) {\n' +
    '    if (float(i) > uBloomLevels) break;\n' +
    '    sum += textureLod(uBloom, fboUV(), float(i)).rgb;\n' +
    '    n += 1.0;\n' +
    '  }\n' +
    '  return n > 0.0 ? sum / n : vec3(0.0);\n' +
    '}\n' +
    'void main() {\n' +
    '  vec3 linear = texture(uLinear, fboUV()).rgb + gatherBloom() * uBloomIntensity;\n' +
    '  fragColor = vec4(applyLUT(shaper(linear)), 1.0);\n' +
    '}';

  function compile(gl, type, src) {
    var s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      throw new Error('RCFMockup shader: ' + gl.getShaderInfoLog(s));
    }
    return s;
  }

  function program(gl, frag) {
    var p = gl.createProgram();
    gl.attachShader(p, compile(gl, gl.VERTEX_SHADER, VERT));
    gl.attachShader(p, compile(gl, gl.FRAGMENT_SHADER, frag));
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
      throw new Error('RCFMockup link: ' + gl.getProgramInfoLog(p));
    }
    return p;
  }

  /*
   * Data textures must arrive byte-for-byte. The <img> path lets the browser
   * premultiply alpha and apply a colour-profile conversion, either of which
   * rewrites the packed UV coordinates -- alpha here carries V's low byte, not
   * opacity. createImageBitmap is the only load path that disables both.
   */
  /* priority:'low' is how this stays out of the page's way. Delaying the whole
     thing behind a timer protected LCP but left the picker missing for seconds;
     telling the browser these are low-priority lets them start early and still
     queue behind anything that matters. Ignored by browsers that do not support
     it, which simply behave as before.
     These options must stay identical to the warm-up's in rcf-mockup-config --
     a fetch that differs in mode or credentials is a different cache entry. */
  var FETCH_OPTS = { mode: 'cors', credentials: 'omit', priority: 'low' };

  function fetchBlob(url) {
    return fetch(url, FETCH_OPTS).then(function (r) {
      if (!r || !r.ok) throw new Error('RCFMockup: ' + (r && r.status) + ' for ' + url);
      return r.blob();
    });
  }

  /*
   * WebKit check. The UV texture carries data in its alpha channel (V's low
   * byte), and createImageBitmap's premultiplyAlpha:'none' option -- which
   * Chromium honours -- is ignored by WebKit: its ImageBitmaps are always
   * premultiplied, and the premultiply/unpremultiply round trip quantises away
   * the low bits of the packed coordinates. On Safari and everything on iOS
   * (Chrome included -- it is WebKit there) that corrupted the UVs and drew the
   * print as streaky dashes. Same family of bug as UNPACK_FLIP_Y_WEBGL being
   * ignored for ImageBitmap sources, which shipped the print upside down.
   *
   * Rather than sniff the browser, measure it: push a 1x1 PNG whose bytes
   * (3,129,250,17) cannot survive a premultiply round trip through the
   * ImageBitmap path and read them back. Exact bytes: keep ImageBitmap (fast,
   * off-thread decode). Anything else: fall back to <img> uploads, where the
   * WebGL spec requires UNPACK_PREMULTIPLY_ALPHA_WEBGL=false to be honoured
   * and WebKit complies.
   */
  var PROBE_PNG = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR4nGNgbvwlCAADmQGQl9SbNgAAAABJRU5ErkJggg==';
  var _bitmapExact = null;
  var _transport = 'untested';

  function bitmapExact() {
    if (_bitmapExact) return _bitmapExact;
    _bitmapExact = fetch(PROBE_PNG).then(function (r) { return r.blob(); })
      .then(function (b) {
        return createImageBitmap(b, { premultiplyAlpha: 'none', colorSpaceConversion: 'none' });
      })
      .then(function (bm) {
        var cv = document.createElement('canvas');
        cv.width = cv.height = 1;
        var gl = cv.getContext('webgl2');
        if (!gl) return true;               // no GL here; the renderer will fail anyway
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
        gl.pixelStorei(gl.UNPACK_COLORSPACE_CONVERSION_WEBGL, gl.NONE);
        var t = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, t);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, bm);
        var f = gl.createFramebuffer();
        gl.bindFramebuffer(gl.FRAMEBUFFER, f);
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, t, 0);
        var px = new Uint8Array(4);
        gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, px);
        var lose = gl.getExtension('WEBGL_lose_context');
        if (lose) lose.loseContext();
        if (bm.close) bm.close();
        var ok = px[0] === 3 && px[1] === 129 && px[2] === 250 && px[3] === 17;
        _transport = ok ? 'imagebitmap (exact)' : 'IMG FALLBACK (bitmap lossy)';
        return ok;
      })
      .catch(function () {                  // options unsupported entirely
        _transport = 'IMG FALLBACK (no options)';
        return false;
      });
    return _bitmapExact;
  }

  function imgFromBlob(b) {
    /* onload, deliberately not decode(): decode() schedules with rendering
       work and never resolves in a hidden or backgrounded renderer, which
       stalled the whole pipeline. onload fires once the data is read, and
       texImage2D decodes synchronously at upload regardless. */
    return new Promise(function (resolve, reject) {
      var u = URL.createObjectURL(b);
      var im = new Image();
      im.onload = function () { URL.revokeObjectURL(u); resolve(im); };
      im.onerror = function () {
        URL.revokeObjectURL(u);
        reject(new Error('RCFMockup: image decode failed'));
      };
      im.src = u;
    });
  }

  function loadBitmap(url) {
    /* The inline config starts these fetches during page parse, seconds before
       this deferred script even runs, and parks the promises here. Taking over
       the in-flight response rather than issuing our own is what makes it exact:
       no second request, no reliance on the HTTP cache having kept the entry. */
    var warm = window.RCF_MOCKUPS && window.RCF_MOCKUPS.__warm;
    var started = warm && warm[url];
    if (started) warm[url] = null;      // a Response body can only be read once

    var blob = started
      ? started.then(function (r) {
          if (!r || !r.ok) throw new Error('warm miss');
          return r.blob();
        }).catch(function () { return fetchBlob(url); })
      : fetchBlob(url);

    return blob.then(function (b) {
      return bitmapExact().then(function (exact) {
        if (exact) {
          return createImageBitmap(b, {
            premultiplyAlpha: 'none',
            colorSpaceConversion: 'none'
          });
        }
        return imgFromBlob(b);
      });
    });
  }

  /*
   * Memoised, and the probe context is released immediately.
   *
   * This used to build a canvas and a WebGL2 context on every call, and callers
   * hit it once per gallery image. Browsers cap live contexts (Chrome near 16)
   * and evict the OLDEST when the cap is passed -- so a gallery-heavy product
   * could quietly kill the real renderer's context, on top of costing ~5-10 ms
   * of main-thread time per call.
   */
  var supportCache = null;

  function isSupported() {
    if (supportCache !== null) return supportCache;
    supportCache = false;
    if (typeof createImageBitmap !== 'function' || typeof fetch !== 'function') return false;
    try {
      var c = document.createElement('canvas');
      c.width = c.height = 1;
      var gl = c.getContext('webgl2');
      supportCache = !!gl;
      if (gl) {
        var lose = gl.getExtension('WEBGL_lose_context');
        if (lose) lose.loseContext();
      }
    } catch (e) {
      supportCache = false;
    }
    return supportCache;
  }

  /*
   * Hand a rendered canvas to a visible <img> without swapping elements, so the
   * box never changes and there is no layout shift.
   *
   * toBlob rather than toDataURL: toDataURL is synchronous on the main thread
   * and yields a 30-60 KB base64 string per thumbnail. The new source is decoded
   * into a throwaway Image first, so the visible element never shows a blank
   * frame mid-swap.
   */
  function blitToImage(canvas, imgEl) {
    // Returns a promise that settles once the src is actually assigned. Callers
    // reveal the picker when every thumbnail has landed, and this chain
    // (encode -> decode -> assign) outlives the render that produced it -- so
    // resolving early makes the last thumbnail look like a failure.
    return new Promise(function (resolve) {
      if (!canvas || !imgEl || !canvas.toBlob) { resolve(false); return; }
      canvas.toBlob(function (blob) {
        if (!blob) { resolve(false); return; }
        var url = URL.createObjectURL(blob);
        var probe = new Image();
        probe.onload = function () {
          var old = imgEl.getAttribute('data-rcf-url');
          imgEl.src = url;
          imgEl.setAttribute('data-rcf-url', url);
          if (old) URL.revokeObjectURL(old);   // re-renders on each new upload
          resolve(true);
        };
        probe.onerror = function () { URL.revokeObjectURL(url); resolve(false); };
        probe.src = url;
      }, 'image/png');
    });
  }

  function Renderer(size) {
    this.canvas = document.createElement('canvas');
    this.canvas.width = this.canvas.height = size || 960;
    var gl = this.canvas.getContext('webgl2', { antialias: false, alpha: false });
    if (!gl) throw new Error('RCFMockup: WebGL2 unavailable');
    this.gl = gl;
    this.floatOk = !!gl.getExtension('EXT_color_buffer_float');

    this.pComposite = program(gl, FRAG_COMPOSITE);
    this.pPrefilter = program(gl, FRAG_PREFILTER);
    this.pFinal = program(gl, FRAG_FINAL);
    this.vao = gl.createVertexArray();

    var self = this;
    this.uc = function (n) { return gl.getUniformLocation(self.pComposite, n); };
    this.up = function (n) { return gl.getUniformLocation(self.pPrefilter, n); };
    this.uf = function (n) { return gl.getUniformLocation(self.pFinal, n); };

    /* Fitted against bloom-on reference renders, NOT copied from the .blend.
       Blender's own values are threshold 0.8 / knee 0.5 / intensity 0.8; these
       differ because mip averaging is not EEVEE's blur pyramid, so they absorb
       the shape difference. Error against the reference is ~2.6/255, against
       32 with no bloom at all and a 2.2 floor -- i.e. bloom is no longer the
       limiting factor. Re-fit if the bake or the blur changes; the references
       are truth_bloom.png in each bake folder. */
    /* Bloom is OFF by default -- a deliberate break from the render farm.
       Measured against Blender ground truth on a dark design (Watercolor
       Wildflower Black): this pipeline at neutral settings matches Blender
       bloom-off to 1/255 median, while bloom lifts pure blacks by +19 here and
       by +32 in EEVEE's own bloom-on render. On dark prints that lift is the
       washed, hazy look customers reported as "fuzzy" -- and the farm's own
       mockups had it worse. Killing bloom is what restores deep blacks.
       The machinery stays; set enabled:true to compare against old renders. */
    this.bloom = { enabled: false, threshold: 0.4, knee: 0.2, clamp: 1.0, intensity: 1.6, levels: 7 };

    /* Deliberate departure from Blender: the mockup is a sales image, and a
       physically faithful one undersells the print -- lit fabric lands ~19%
       below the flat swatch, because the lighting peaks at 0.55 linear and the
       High Contrast output look drops mid-tones further.

       Calibrated across gingham, stripe, dot and dark saturated designs:
       brightness 1.02-1.07x the flat design, clipping 0-1%. The low knee plus
       strong shoulder is what keeps highlights intact -- a plain gain reaches
       the same brightness but blows out 36-46% of a dotted design, flattening
       the fold shading that makes it read as 3D.

       Set gain 1.0 / shoulder 0.0 to reproduce Blender exactly. */
    /* backBoost compensates the backdrop for bloom being off: bloom used to
       blow the shiplap out to clipped white (median 255), and without it the
       set reads grey (223). 1.3 lands at 251 -- white, but the plank lines
       survive. Applied only where the garment mask is empty, so it cannot
       touch the fabric's blacks. Fitted numerically; purely aesthetic. */
    /* knee 0.5: the shoulder curve has a hard ceiling of knee + 1/shoulder,
       and at the old knee of 0.35 that ceiling was 0.68 linear (~217 sRGB) --
       no pixel on any garment could ever reach white, so white designs
       rendered as grey garments on a white set. Bloom used to add light back
       on top and mask it; with bloom off the cap showed. At 0.5 the ceiling is
       0.83 linear (~250 display): measured on a pure-white design, garment
       whites hit median 245-246 with 0.0% clipping -- white, but every shading
       gradation survives -- while a colourful design's mids move by 2/255.
       Raising the knee further blows 20-70% of a white garment to flat 255. */
    /* white 1.08: a post-curve scale calibrated against the user's own
       measurement -- design white 253, mockup white 247. The shoulder is
       asymptotic, so no amount of gain reaches the last few digits (gain 2.0
       still capped p50 at 248 while dragging mids +14%); a small display-side
       scale gets there directly. At 1.08 a lit white area measures 253 --
       matching the design -- with 1.7% clip, mids +4/255 ("a bit brighter",
       as requested), blacks +1. At 1.10 clipping jumps to 20%: that is the
       cliff, do not raise this casually. */
    this.fabric = { gain: 1.55, knee: 0.5, shoulder: 3.0, backBoost: 1.3, white: 1.08 };
    this.seamLimit = 0.02;
    this.tex = {};
    this.targets = null;
    this.manifest = null;
    this.lutMeta = null;
    this.ready = false;
  }

  Renderer.prototype._tex = function (image, opts) {
    var gl = this.gl;
    opts = opts || {};
    var t = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, t);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, !!opts.flipY);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    gl.pixelStorei(gl.UNPACK_COLORSPACE_CONVERSION_WEBGL, gl.NONE);
    gl.texImage2D(gl.TEXTURE_2D, 0, opts.srgb ? gl.SRGB8_ALPHA8 : gl.RGBA8,
      gl.RGBA, gl.UNSIGNED_BYTE, image);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    var wrap = opts.repeat ? gl.REPEAT : gl.CLAMP_TO_EDGE;
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, wrap);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, wrap);
    if (opts.nearest) {
      // Coordinate data: filtering blends neighbouring UVs into nonsense.
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    } else if (opts.mips) {
      gl.generateMipmap(gl.TEXTURE_2D);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      var aniso = gl.getExtension('EXT_texture_filter_anisotropic');
      if (aniso) {
        gl.texParameterf(gl.TEXTURE_2D, aniso.TEXTURE_MAX_ANISOTROPY_EXT,
          Math.min(8, gl.getParameter(aniso.MAX_TEXTURE_MAX_ANISOTROPY_EXT)));
      }
    } else {
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    }
    return t;
  };

  Renderer.prototype._ensureTargets = function () {
    var gl = this.gl, size = this.canvas.width;
    if (this.targets && this.targets.size === size) return this.targets;
    if (this.targets) {
      gl.deleteTexture(this.targets.linear);
      gl.deleteTexture(this.targets.bloom);
      gl.deleteFramebuffer(this.targets.fboLinear);
      gl.deleteFramebuffer(this.targets.fboBloom);
    }
    var fmt = this.floatOk ? gl.RGBA16F : gl.RGBA8;
    var type = this.floatOk ? gl.HALF_FLOAT : gl.UNSIGNED_BYTE;
    function make(mips) {
      var t = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, t);
      gl.texImage2D(gl.TEXTURE_2D, 0, fmt, size, size, 0, gl.RGBA, type, null);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER,
        mips ? gl.LINEAR_MIPMAP_LINEAR : gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      if (mips) gl.generateMipmap(gl.TEXTURE_2D);
      return t;
    }
    var linear = make(false), bloom = make(true);
    var fboLinear = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fboLinear);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, linear, 0);
    var fboBloom = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fboBloom);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, bloom, 0);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    this.targets = { size: size, linear: linear, bloom: bloom,
                     fboLinear: fboLinear, fboBloom: fboBloom };
    return this.targets;
  };

  /**
   * config = {
   *   manifest: {...},                    // inlined by Liquid, not fetched
   *   lut: { size, log_min, log_max, url },
   *   textures: { uv, lit, spec, back }   // absolute CDN urls
   * }
   */
  Renderer.prototype.load = function (config) {
    var self = this, gl = this.gl;
    this.ready = false;

    // The LUT is the same file for every garment. Re-fetching, re-decoding a
    // 4096x64 PNG and re-uploading it once per garment cost four needless
    // round trips and four texture uploads on every thumbnail pass.
    var lutSame = !!(self.tex.lut && self._lutUrl === config.lut.url);

    ['uv', 'lit', 'back', 'spec'].forEach(function (k) {
      if (self.tex[k]) { gl.deleteTexture(self.tex[k]); self.tex[k] = null; }
    });
    if (!lutSame && self.tex.lut) { gl.deleteTexture(self.tex.lut); self.tex.lut = null; }

    // config.bitmaps: already-decoded, keyed by URL. renderThumbnails fills this
    // so all five garments' textures download at once instead of in five
    // sequential waves -- on a high-latency connection that is five round trips
    // collapsed into one.
    var pre = config.bitmaps;
    function get(url) {
      return (pre && pre[url]) ? Promise.resolve(pre[url]) : loadBitmap(url);
    }

    var t = config.textures;
    var jobs = [get(t.uv), get(t.lit), get(t.back),
                lutSame ? Promise.resolve(null) : get(config.lut.url)];
    var hasSpec = !!t.spec;
    if (hasSpec) jobs.push(get(t.spec));

    return Promise.all(jobs).then(function (imgs) {
      self.manifest = config.manifest;
      self.lutMeta = config.lut;          // must stay unconditional: render() reads it
      self.tex.uv = self._tex(imgs[0], { nearest: true });
      self.tex.lit = self._tex(imgs[1], { srgb: true, mips: true });
      self.tex.back = self._tex(imgs[2], { srgb: true, mips: true });
      if (imgs[3]) { self.tex.lut = self._tex(imgs[3], {}); self._lutUrl = config.lut.url; }
      self.tex.spec = hasSpec ? self._tex(imgs[4], { srgb: true, mips: true }) : null;
      self.ready = !!self.tex.pattern;
      return self;
    });
  };

  /** Release everything, including the GL context. */
  Renderer.prototype.dispose = function () {
    var gl = this.gl, self = this;
    try {
      ['uv', 'lit', 'back', 'spec', 'lut', 'pattern'].forEach(function (k) {
        if (self.tex[k]) gl.deleteTexture(self.tex[k]);
      });
      this.tex = {};
      if (this.targets) {
        gl.deleteTexture(this.targets.linear);
        gl.deleteTexture(this.targets.bloom);
        gl.deleteFramebuffer(this.targets.fboLinear);
        gl.deleteFramebuffer(this.targets.fboBloom);
        this.targets = null;
      }
      [this.pComposite, this.pPrefilter, this.pFinal].forEach(function (p) {
        if (p) gl.deleteProgram(p);
      });
      if (this.vao) gl.deleteVertexArray(this.vao);
      var lose = gl.getExtension('WEBGL_lose_context');
      if (lose) lose.loseContext();
    } catch (e) {}
    this.ready = false;
  };

  /**
   * The print itself, from an already-decoded source: an <img>, ImageBitmap or
   * canvas. The custom-upload page holds the design as a data: URL it has
   * already loaded, so re-fetching it would be wasteful.
   */
  Renderer.prototype.setPatternSource = function (source) {
    if (!source) return this;
    if (this.tex.pattern) this.gl.deleteTexture(this.tex.pattern);
    // No flipY here: the shader flips V, so <img>, canvas and ImageBitmap
    // sources all land the same way up.
    this.tex.pattern = this._tex(source, { srgb: true, repeat: true, mips: true });
    this.ready = !!this.tex.uv;
    return this;
  };

  /** The print itself, fetched from a URL. */
  Renderer.prototype.setPatternUrl = function (url) {
    var self = this;
    return loadBitmap(url).then(function (bmp) { return self.setPatternSource(bmp); });
  };

  /**
   * inchesPerRepeat: physical width of one repeat of the design on the fabric,
   * i.e. the template's imgwinch. Returns the canvas to blit.
   */
  Renderer.prototype.render = function (inchesPerRepeat) {
    var gl = this.gl, m = this.manifest, i;
    if (!this.ready || !m || !(inchesPerRepeat > 0)) return null;
    var size = this.canvas.width;
    var T = this._ensureTargets();
    gl.bindVertexArray(this.vao);

    // --- pass 1: composite, linear
    gl.bindFramebuffer(gl.FRAMEBUFFER, T.fboLinear);
    gl.viewport(0, 0, size, size);
    gl.useProgram(this.pComposite);
    var units = [['uUV', this.tex.uv], ['uLit', this.tex.lit],
      ['uSpec', this.tex.spec || this.tex.lit], ['uBack', this.tex.back],
      ['uPattern', this.tex.pattern]];
    for (i = 0; i < units.length; i++) {
      gl.activeTexture(gl.TEXTURE0 + i);
      gl.bindTexture(gl.TEXTURE_2D, units[i][1]);
      gl.uniform1i(this.uc(units[i][0]), i);
    }
    var p = m.uv_pack;
    gl.uniform2f(this.uc('uUVOffset'), p.u_offset, p.v_offset);
    gl.uniform2f(this.uc('uUVScale'), p.u_scale, p.v_scale);
    gl.uniform1f(this.uc('uLitK'), m.exposure.lit);
    gl.uniform1f(this.uc('uSpecK'), m.exposure.spec);
    gl.uniform1f(this.uc('uBackK'), m.exposure.back * (this.fabric.backBoost || 1.0));
    gl.uniform1f(this.uc('uRepeats'), m.inches_per_uv / inchesPerRepeat);
    var rot = (m.material_mapping && m.material_mapping.rotation_deg)
      ? m.material_mapping.rotation_deg[2] : 0;
    gl.uniform1f(this.uc('uRotation'), rot * Math.PI / 180);
    gl.uniform1f(this.uc('uSeamLimit'), this.seamLimit);
    gl.uniform1f(this.uc('uFabricGain'), this.fabric.gain);
    gl.uniform1f(this.uc('uFabricKnee'), this.fabric.knee);
    gl.uniform1f(this.uc('uShoulder'), this.fabric.shoulder);
    gl.uniform1f(this.uc('uFabricWhite'), this.fabric.white || 1.0);
    gl.uniform1i(this.uc('uHasSpec'), this.tex.spec ? 1 : 0);
    gl.drawArrays(gl.TRIANGLES, 0, 3);

    // --- pass 2: bloom prefilter, then build the pyramid via mipmaps
    var B = this.bloom;
    var knee = Math.max(B.knee * B.threshold, 1e-5);
    gl.bindFramebuffer(gl.FRAMEBUFFER, T.fboBloom);
    gl.viewport(0, 0, size, size);
    gl.useProgram(this.pPrefilter);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, T.linear);
    gl.uniform1i(this.up('uSrc'), 0);
    gl.uniform4f(this.up('uCurve'), B.threshold - knee, 2 * knee, 0.25 / knee, B.threshold);
    gl.uniform1f(this.up('uClamp'), B.clamp);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    gl.bindTexture(gl.TEXTURE_2D, T.bloom);
    gl.generateMipmap(gl.TEXTURE_2D);

    // --- pass 3: add bloom, display transform, to the visible canvas
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, size, size);
    gl.useProgram(this.pFinal);
    var fu = [['uLinear', T.linear], ['uBloom', T.bloom], ['uLUT', this.tex.lut]];
    for (i = 0; i < fu.length; i++) {
      gl.activeTexture(gl.TEXTURE0 + i);
      gl.bindTexture(gl.TEXTURE_2D, fu[i][1]);
      gl.uniform1i(this.uf(fu[i][0]), i);
    }
    gl.uniform1f(this.uf('uLutSize'), this.lutMeta.size);
    gl.uniform2f(this.uf('uLogRange'), this.lutMeta.log_min, this.lutMeta.log_max);
    gl.uniform1f(this.uf('uBloomIntensity'), B.enabled ? B.intensity : 0.0);
    gl.uniform1f(this.uf('uBloomLevels'), B.levels);
    gl.drawArrays(gl.TRIANGLES, 0, 3);

    return this.canvas;
  };

  /*
   * Sidebar pickers, rendered from the live design.
   *
   * This is what lets the Blender render farm be retired: the thumbnails no
   * longer have to be pre-rendered per product, they are drawn here from the
   * same design the flat preview uses. Backed by 240px texture sets (~760 KB
   * for all five) rather than the 960px ones, since a 128px thumbnail cannot
   * show more.
   *
   * Rendered once at the design's default scale and left there -- only the main
   * image tracks the scale slider.
   *
   * opts = {
   *   objects: [{ key, manifest, textures }],   // thumb-res variants
   *   lut, patternSource, inchesPerRepeat,
   *   size, onReady(key, canvas), onError(key, err)
   * }
   */
  var thumbRenderer = null;

  function renderThumbnails(opts) {
    var size = opts.size || 128;
    if (thumbRenderer && thumbRenderer.canvas.width !== size) {
      thumbRenderer.dispose();               // otherwise its context is abandoned
      thumbRenderer = null;
    }
    if (!thumbRenderer) thumbRenderer = new Renderer(size);
    var r = thumbRenderer;
    r.setPatternSource(opts.patternSource);

    /* Download everything at once, then render one garment at a time.
       Fetching and rendering are different bottlenecks: the GPU work is a single
       draw per garment and wants to be serial, but doing the fetches serially
       too meant five round trips in sequence. On a slow connection that was most
       of the wait before the picker appeared. */
    var urls = [opts.lut.url];
    opts.objects.forEach(function (o) {
      ['uv', 'lit', 'back', 'spec'].forEach(function (p) {
        if (o.textures[p]) urls.push(o.textures[p]);
      });
    });
    var bitmaps = {};
    var pending = [];          // in-flight onReady work, awaited at the end
    var warm = Promise.all(urls.map(function (u) {
      return loadBitmap(u).then(function (bm) { bitmaps[u] = bm; })
                          .catch(function () { /* load() will retry and report */ });
    }));

    return warm.then(function () {
      return opts.objects.reduce(function (chain, o) {
        return chain.then(function () {
          // load() replaces the pass textures but keeps the pattern, so the
          // design is uploaded to the GPU once rather than once per garment.
          return r.load({ manifest: o.manifest, lut: opts.lut,
                          textures: o.textures, bitmaps: bitmaps })
          .then(function () {
            var out = r.render(opts.inchesPerRepeat);
            if (!out) throw new Error('render returned null');
            var c = document.createElement('canvas');
            c.width = c.height = size;
            c.getContext('2d').drawImage(out, 0, 0, size, size);
            /* Collect, do not await. onReady's work (PNG encode, decode, assign)
               is asynchronous and independent per garment, so awaiting it inside
               the chain made five of them run end to end. The renders stay
               serial -- one GPU context -- while the blits overlap, and the
               returned promise still means "every thumbnail has landed". */
            if (opts.onReady) {
              var p = opts.onReady(o.key, c);
              if (p && typeof p.then === 'function') pending.push(p);
            }
          })
          .catch(function (e) {
            if (opts.onError) opts.onError(o.key, e);
          });
        });
      }, Promise.resolve());
    }).then(function () {
      return Promise.all(pending);      // every blit has assigned its src
    }).then(function () {
      // The decoded bitmaps are on the GPU now; drop the CPU-side copies.
      Object.keys(bitmaps).forEach(function (u) {
        if (bitmaps[u] && bitmaps[u].close) bitmaps[u].close();
      });
      bitmaps = {};
    });
  }

  return {
    isSupported: isSupported,
    create: function (size) { return new Renderer(size); },
    renderThumbnails: renderThumbnails,
    // Hands a rendered canvas to a visible <img> without swapping elements.
    blitToImage: blitToImage,
    // Exposed because a page's own <img> of the design is usually loaded
    // without crossOrigin, and WebGL cannot read a tainted image.
    loadImage: loadBitmap,
    // Which texture upload path the probe chose -- surfaced in the on-page
    // debug panel so a device can report it without a console.
    transport: function () { return _transport; },
    // Test hook only: force the <img> path so the fallback can be exercised
    // on a browser whose ImageBitmap is exact.
    __forceImgTransport: function () {
      _bitmapExact = Promise.resolve(false);
      _transport = 'IMG FALLBACK (forced)';
    }
  };
})();
